import { Controller } from "@hotwired/stimulus"

// Switches between named panels.
// Works with both a <select> (legacy) and pill-style <button> tabs.
//
// Targets:
//   select  – optional legacy <select> (data-variant-target="select")
//   tab     – optional <button> tabs    (data-variant-target="tab")
//   panel   – content panels            (data-variant-target="panel")
//
// Each target carries data-variant-key with a matching value so the
// controller can pair a tab/option to a panel.

export default class extends Controller {
  static targets = ["select", "tab", "panel", "input"]
  static values = { disableInactive: Boolean }

  connect() {
    this.#syncAll(this.#activeKey())
  }

  // Triggered by a <select data-action="variant#show">
  show() {
    this.#syncAll(this.selectTarget.value)
  }

  // Triggered by a tab button: data-action="variant#showByTab"
  showByTab(event) {
    const key = this.#keyOf(event.currentTarget)
    this.#syncAll(key)
  }

  navigateTabs(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return

    event.preventDefault()

    const currentIndex = this.tabTargets.indexOf(event.currentTarget)
    let nextIndex

    if (event.key === "Home") {
      nextIndex = 0
    } else if (event.key === "End") {
      nextIndex = this.tabTargets.length - 1
    } else if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + this.tabTargets.length) % this.tabTargets.length
    } else {
      nextIndex = (currentIndex + 1) % this.tabTargets.length
    }

    const nextTab = this.tabTargets[nextIndex]
    this.#syncAll(this.#keyOf(nextTab))
    nextTab.focus()
  }

  // ── private ──────────────────────────────────────────────────────

  #activeKey() {
    // Prefer the select's current value when present
    if (this.hasSelectTarget) return this.selectTarget.value
    // Preserve server-rendered state after redirects or prefill
    const activeTab = this.tabTargets.find((tab) => tab.classList.contains("active"))
    if (activeTab) return this.#keyOf(activeTab)
    // Fall back to first tab
    if (this.hasTabTarget) return this.#keyOf(this.tabTargets[0])
    return "0"
  }

  #keyOf(el) {
    return el.dataset.variantKey ?? "0"
  }

  #syncAll(selectedKey) {
    // Panels: show the matching one
    this.panelTargets.forEach((panel) => {
      const active = this.#keyOf(panel) === selectedKey
      panel.classList.toggle("hidden", !active)
      panel.setAttribute("aria-hidden", String(!active))

      if (this.disableInactiveValue) {
        panel.querySelectorAll("input, select, textarea, button").forEach((control) => {
          control.disabled = !active
        })
      }
    })

    // Tabs: mark active
    this.tabTargets.forEach((tab) => {
      const active = this.#keyOf(tab) === selectedKey
      tab.classList.toggle("active", active)
      tab.setAttribute("aria-selected", String(active))
      tab.setAttribute("tabindex", active ? "0" : "-1")
    })

    // Select: keep in sync if present
    if (this.hasSelectTarget) {
      this.selectTarget.value = selectedKey
    }

    this.inputTargets.forEach((input) => {
      input.value = selectedKey
    })
  }
}
